import { z } from "zod";

export const INTERNAL_SCHEMA_VERSION = "1.0.0" as const;
export const CONTRACT_SCHEMA_VERSION = "1.0.0" as const;

export type ISODate = string;
export type ISODateTime = string;
export type EntityId = string;

export type LifecycleStatus =
  | "draft"
  | "active"
  | "confirmed"
  | "completed"
  | "verified"
  | "falsified"
  | "overdue"
  | "cancelled"
  | "breached"
  | "superseded"
  | "archived"
  | "deleted";

export type DeletionPolicy = "soft_then_hard" | "retain" | "hard_on_request";
export type DeletionState = "active" | "soft_deleted" | "hard_deleted";

export type SourceKind =
  "user_input" | "board_import" | "local_rule" | "ai_assisted" | "derived" | "migration";

export interface EntitySource {
  kind: SourceKind;
  referenceIds: EntityId[];
  actorId?: string;
}

export interface EntityMetadata {
  id: EntityId;
  schemaVersion: string;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  status: LifecycleStatus;
  source: EntitySource;
  exportAllowed: boolean;
  deletionPolicy: DeletionPolicy;
  deletionState: DeletionState;
}

export const entityMetadataSchema = z.object({
  id: z.uuid(),
  schemaVersion: z.string().min(1),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  status: z.enum([
    "draft",
    "active",
    "confirmed",
    "completed",
    "verified",
    "falsified",
    "overdue",
    "cancelled",
    "breached",
    "superseded",
    "archived",
    "deleted",
  ]),
  source: z.object({
    kind: z.enum([
      "user_input",
      "board_import",
      "local_rule",
      "ai_assisted",
      "derived",
      "migration",
    ]),
    referenceIds: z.array(z.uuid()),
    actorId: z.string().min(1).optional(),
  }),
  exportAllowed: z.boolean(),
  deletionPolicy: z.enum(["soft_then_hard", "retain", "hard_on_request"]),
  deletionState: z.enum(["active", "soft_deleted", "hard_deleted"]),
});

export function createEntityMetadata(
  source: EntitySource,
  options: {
    id?: string;
    now?: string;
    status?: LifecycleStatus;
    exportAllowed?: boolean;
    deletionPolicy?: DeletionPolicy;
  } = {},
): EntityMetadata {
  const now = options.now ?? new Date().toISOString();
  return {
    id: options.id ?? crypto.randomUUID(),
    schemaVersion: INTERNAL_SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    status: options.status ?? "draft",
    source,
    exportAllowed: options.exportAllowed ?? false,
    deletionPolicy: options.deletionPolicy ?? "soft_then_hard",
    deletionState: "active",
  };
}
